// One place that files an email into a folder: calls the API, tells the
// rest of the screen, and offers Undo. Used by the sidebar's drop targets
// and the reading pane's "Move" button so both behave identically.
//
// Reversible by default (CLAUDE.md): the success toast carries an Undo
// that moves the message back to where it came from. Graph issues a new
// message id on every move, so Undo uses the id returned by the first.

import { useCallback } from "react";

import { api, ApiError } from "../../api";
import { emitEmailMove } from "../../utils/email-move";
import { useToast } from "../Toast";

export interface MoveEmailArgs {
  messageId: string;
  /** Id (or well-known name) Graph accepts for the destination. */
  destinationFolderId: string;
  /** Human name for the toast: "Moved to Amazon." */
  destinationName: string;
  /** URL key of the source folder, when known. Enables Undo. */
  sourceFolderKey: string | null;
}

export function useMoveEmail(): (args: MoveEmailArgs) => Promise<boolean> {
  const toast = useToast();
  return useCallback(
    async ({ messageId, destinationFolderId, destinationName, sourceFolderKey }) => {
      // Optimistic: the row leaves the list now; a failure puts it back
      // via a "restored" event (a refetch of the source folder).
      emitEmailMove({ type: "moved", messageId, sourceFolderKey });
      try {
        const { newId } = await api.moveEmail(messageId, destinationFolderId);
        toast.success(`Moved to ${destinationName}.`, {
          ...(sourceFolderKey
            ? {
                action: {
                  label: "Undo",
                  onClick: () => {
                    void (async () => {
                      try {
                        await api.moveEmail(newId, sourceFolderKey);
                        emitEmailMove({ type: "restored", sourceFolderKey });
                        toast.success("Moved back.");
                      } catch (err) {
                        toast.error(
                          err instanceof ApiError ? err.message : "Couldn't undo the move.",
                        );
                      }
                    })();
                  },
                },
              }
            : {}),
        });
        return true;
      } catch (err) {
        if (sourceFolderKey) emitEmailMove({ type: "restored", sourceFolderKey });
        toast.error(err instanceof ApiError ? err.message : "Couldn't move the email.");
        return false;
      }
    },
    [toast],
  );
}
